import { Texture } from "pixi.js"
import { wallArtAtlas } from "../sprites/canvas_atlas"

export interface WallJointPatch { texture: Texture; dy: number }

/** Paint an upper post only where a lower wall already has ink. Its silhouette and depth stay intact. */
export const paintWallJoint = (base: Uint8ClampedArray, upper: ArrayLike<number>, width: number, height: number, dy: number): void => {
    for (let y = 0; y < height; y++) {
        const sy = y - dy
        if (sy < 0 || sy >= height) continue
        for (let x = 0; x < width; x++) {
            const dst = (y * width + x) * 4, src = (sy * width + x) * 4
            if (!base[dst + 3] || !upper[src + 3]) continue
            const alpha = upper[src + 3] / 255
            for (let c = 0; c < 3; c++) base[dst + c] = Math.round(upper[src + c] * alpha + base[dst + c] * (1 - alpha))
        }
    }
}

const pixels = (texture: Texture) => {
    const source = (texture.baseTexture.resource as any)?.source
    if (!source || !texture.baseTexture.valid) return undefined
    const canvas = document.createElement("canvas")
    canvas.width = texture.width; canvas.height = texture.height
    const context = canvas.getContext("2d")
    if (!context) return undefined
    const f = texture.frame, trim = texture.trim
    context.drawImage(source, f.x, f.y, f.width, f.height, trim?.x || 0, trim?.y || 0, f.width, f.height)
    return { canvas, context, image: context.getImageData(0, 0, canvas.width, canvas.height) }
}

const cache = new WeakMap<Texture, Map<string, Texture>>()
const ids = new WeakMap<Texture, number>()
let nextId = 0
const id = (texture: Texture) => { if (!ids.has(texture)) ids.set(texture, ++nextId); return ids.get(texture) }

/** Recessed storeys may sort behind a lower wall. Retain their end-face art in that overlap. */
export const withWallJointOverlap = (texture: Texture, patches: WallJointPatch[]): Texture => {
    if (!patches.length) return texture
    const key = patches.map(p => `${id(p.texture)}:${p.dy}`).join("|")
    const variants = cache.get(texture) ?? new Map<string, Texture>()
    cache.set(texture, variants)
    const known = variants.get(key)
    if (known && !known.baseTexture.destroyed) return known
    const base = pixels(texture)
    if (!base) return texture
    for (const patch of patches) {
        const upper = pixels(patch.texture)
        // Retry on the next repaint if an atlas is still loading.
        if (!upper || upper.canvas.width !== base.canvas.width || upper.canvas.height !== base.canvas.height) return texture
        paintWallJoint(base.image.data, upper.image.data, base.canvas.width, base.canvas.height, patch.dy)
    }
    base.context.putImageData(base.image, 0, 0)
    // On a shared page (wallArtAtlas), so a wall of distinct cells batches together.
    const result = wallArtAtlas.put({ width: base.canvas.width, height: base.canvas.height, data: base.image.data }) ?? Texture.from(base.canvas)
    variants.set(key, result)
    return result
}
